import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, Paperclip, Send } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import { fmtXof } from './recMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/recouvrement`;

/** Aperçu éditable d'une relance (email ou WhatsApp) avant envoi réel. */
export const RelanceModal = ({ open, invoiceRef, channel = 'email', onClose, onSent }) => {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachPdf, setAttachPdf] = useState(true);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/invoice/${encodeURIComponent(invoiceRef)}/relance-preview`,
          { params: { channel } });
        if (cancelled) return;
        setDraft(data);
        setTo(data.to || '');
        setSubject(data.subject || '');
        setMessage(data.message || '');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Brouillon de relance indisponible');
        onClose();
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, invoiceRef, channel, onClose]);

  const send = async () => {
    setSending(true);
    try {
      const url = `${API}/invoice/${encodeURIComponent(invoiceRef)}/relance-${channel === 'email' ? 'email' : 'whatsapp'}`;
      const payload = channel === 'email'
        ? { to: to.trim(), subject: subject.trim(), message, attachPdf }
        : { to: to.trim(), message };
      const { data } = await axios.post(url, payload);
      toast.success(channel === 'email'
        ? `Relance envoyée à ${data.to}${data.pdfAttached ? ' (facture PDF jointe)' : ''}`
        : `Relance WhatsApp mise en file pour ${data.to}`);
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Envoi impossible', { duration: 8000 });
    } finally { setSending(false); }
  };

  const isEmail = channel === 'email';
  const canSend = to.trim().length > 4 && message.trim().length > 10 && (!isEmail || subject.trim().length > 2);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="rec-relance-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-emerald-600" />
            {isEmail ? 'Relance par email' : 'Relance WhatsApp'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {draft
              ? `${draft.clientName} · ${fmtXof(draft.amountDue)} restant dû · ${draft.daysOverdue > 0 ? `${draft.daysOverdue} jours de retard` : 'à jour'}`
              : 'Préparation du message…'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{isEmail ? 'Destinataire' : 'Numéro WhatsApp'}</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} className="h-9 text-sm"
                placeholder={isEmail ? 'client@exemple.com' : '+225 07 00 00 00 00'}
                data-testid="rec-relance-to" />
              {!to && (
                <p className="mt-1 text-[11px] text-amber-600">
                  Aucun contact enregistré pour ce client — saisissez-le ici.
                </p>
              )}
            </div>
            {isEmail && (
              <div>
                <Label className="text-xs">Objet</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                  className="h-9 text-sm" data-testid="rec-relance-subject" />
              </div>
            )}
            <div>
              <Label className="text-xs">Message</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={11}
                className="text-xs leading-relaxed" data-testid="rec-relance-message" />
            </div>
            {isEmail && (
              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
                <span className="flex items-center gap-2 text-xs text-gray-700">
                  <Paperclip className="h-3.5 w-3.5 text-gray-400" /> Joindre le PDF de la facture
                </span>
                <Switch checked={attachPdf} onCheckedChange={setAttachPdf} data-testid="rec-relance-attach" />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="rec-relance-cancel">Annuler</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={send}
            disabled={sending || loading || !canSend} data-testid="rec-relance-send">
            {sending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
            Envoyer la relance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RelanceModal;
